import type { FC } from 'react';
import { useState } from 'react';

import { ContextMenu, Dialog, IconButton, Pill } from '@zen/DesignSystem';
import type { MenuItemType, TableColumn } from '@zen/DesignSystem/types';
import type { IconName } from '@zen/Styleguide';

import type { Webhook } from './types';

interface WebhookActionsProps {
  onDelete: (id: string) => void;
  onEdit: (webhook: Webhook) => void;
  onToggle: (webhookId: string, isDisabled: boolean) => void;
  webhook: Webhook;
}

const WebhookActions: FC<WebhookActionsProps> = ({ webhook, onEdit, onDelete, onToggle }) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);

  const toggleIcon: IconName = webhook.isDisabled ? 'zicon-visible' : 'zicon-hidden';
  const toggleTitle: string = webhook.isDisabled ? 'Enable' : 'Disable';

  const menuItems: MenuItemType[] = [
    { icon: 'zicon-edit' as IconName, label: 'Edit', onClick: () => onEdit(webhook) },
    { icon: 'zicon-trash' as IconName, label: 'Delete', onClick: () => setShowDeleteConfirmation(true) }
  ];

  return (
    <>
      <Dialog
        confirmLabel="Delete"
        header="Delete Webhook?"
        isOpen={showDeleteConfirmation}
        message="Removing this means you will no longer be notified about the topics you subscribed to. This action cannot be undone."
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          setShowDeleteConfirmation(false);
          onDelete(webhook.id);
        }}
      />
      <div className="flex space-x-2">
        <IconButton
          icon={toggleIcon}
          onClick={() => onToggle(webhook.id, webhook.isDisabled)}
          size="medium"
          title={toggleTitle}
          variant="ghost"
        />
        <ContextMenu inline={false} items={menuItems} togglerSize="medium" />
      </div>
    </>
  );
};

interface TableParams {
  accountHasBusinessUnits: boolean;
  canManage: boolean;
  onDelete: (id: string) => void;
  onEdit: (webhook: Webhook) => void;
  onToggle: (webhookId: string, isDisabled: boolean) => void;
}

export const getColumns = ({
  accountHasBusinessUnits,
  canManage,
  onEdit,
  onDelete,
  onToggle
}: TableParams): TableColumn<Webhook>[] => {
  const nameColumn: TableColumn<Webhook> = {
    key: 'name',
    title: 'Name',
    sortable: false,
    width: 300
  };

  const callbackUrlColumn: TableColumn<Webhook> = {
    key: 'callbackUrl',
    title: 'Callback URL',
    render: (_, { callbackUrl }: Webhook) => <div className="font-mono text-xs">{callbackUrl}</div>,
    sortable: false
  };

  const statusColumn: TableColumn<Webhook> = {
    key: 'status',
    title: 'Status',
    render: (_, { isDisabled }: Webhook) => (
      <Pill type={isDisabled ? 'problem' : 'success'}>{isDisabled ? 'Disabled' : 'Enabled'}</Pill>
    ),
    sortable: false
  };

  const businessUnitColumn: TableColumn<Webhook> = {
    key: 'businessUnit',
    title: 'Business unit',
    render: (_, { account }: Webhook) => account?.tradingName,
    sortable: false
  };

  const actionsColumn: TableColumn<Webhook> = {
    key: 'actions',
    sortable: false,
    fixed: 'right',
    render: (_, webhook) => <WebhookActions onDelete={onDelete} onEdit={onEdit} onToggle={onToggle} webhook={webhook} />
  };

  return [
    nameColumn,
    callbackUrlColumn,
    ...(accountHasBusinessUnits ? [businessUnitColumn] : []),
    statusColumn,
    ...(canManage ? [actionsColumn] : [])
  ];
};
